/**
 * RatesTab Component
 *
 * Full-width fee schedule view with tier selector and methodology banner.
 * Shows ALL codes from the payer's data, not just a hardcoded canonical list.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import type { PayerContract, CodeRate } from '../types';
import { CitationChip } from './CitationChip';

/**
 * Known CPT descriptions for display (canonical order for sorting).
 * Codes not in this list still appear -- they just sort to the end.
 */
const KNOWN_CODES: Record<string, { description: string; order: number }> = {
  '90785': { description: 'Interactive complexity add-on', order: 14 },
  '90791': { description: 'Psychiatric diagnostic eval', order: 1 },
  '90792': { description: 'Psych diagnostic eval w/ medical', order: 2 },
  '90832': { description: 'Psychotherapy, 30 min', order: 3 },
  '90833': { description: 'Psychotherapy add-on, 30 min', order: 7 },
  '90834': { description: 'Psychotherapy, 45 min', order: 4 },
  '90836': { description: 'Psychotherapy add-on, 45 min', order: 8 },
  '90837': { description: 'Psychotherapy, 60 min', order: 5 },
  '90838': { description: 'Psychotherapy add-on, 60 min', order: 9 },
  '90839': { description: 'Crisis, first 60 min', order: 10 },
  '90840': { description: 'Crisis, each add-on 30 min', order: 11 },
  '90846': { description: 'Family therapy w/o patient', order: 12 },
  '90847': { description: 'Family therapy w/ patient', order: 13 },
  '90853': { description: 'Group psychotherapy', order: 15 },
  '96130': { description: 'Psych testing eval', order: 20 },
  '96131': { description: 'Psych testing eval add-on', order: 21 },
  '96132': { description: 'Neuropsych testing eval', order: 22 },
  '96133': { description: 'Neuropsych testing eval add-on', order: 23 },
  '96136': { description: 'Psych/neuropsych test admin', order: 24 },
  '96137': { description: 'Psych/neuropsych test admin add-on', order: 25 },
  '96138': { description: 'Psych/neuropsych tech admin', order: 26 },
  '96139': { description: 'Psych/neuropsych tech admin add-on', order: 27 },
  '99202': { description: 'E/M new patient level 2', order: 30 },
  '99203': { description: 'E/M new patient level 3', order: 31 },
  '99204': { description: 'E/M new patient level 4', order: 32 },
  '99205': { description: 'E/M new patient level 5', order: 33 },
  '99212': { description: 'E/M established level 2', order: 34 },
  '99213': { description: 'E/M established level 3', order: 35 },
  '99214': { description: 'E/M established level 4', order: 36 },
  '99215': { description: 'E/M established level 5', order: 37 },
};

function codeOrder(code: string): number {
  return KNOWN_CODES[code]?.order ?? 900;
}

function codeDescription(code: string, fromData?: string): string {
  return KNOWN_CODES[code]?.description || fromData || code;
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '\u2014';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

// ─── Build unique sorted code list from payer data ───────────────────────────

function safeSort(a: { code: string }, b: { code: string }): number {
  return codeOrder(a.code) - codeOrder(b.code) || (a.code || '').localeCompare(b.code || '');
}

function buildCodeList(rates: CodeRate[], tierFilter?: string): { code: string; description: string }[] {
  const seen = new Map<string, string>(); // code -> description
  rates.forEach(r => {
    if (!r.cpt_hcpcs) return; // skip null/empty codes
    if (tierFilter && r.provider_tier !== tierFilter) return;
    if (!seen.has(r.cpt_hcpcs)) {
      seen.set(r.cpt_hcpcs, r.description);
    }
  });
  return Array.from(seen.entries())
    .map(([code, desc]) => ({ code, description: codeDescription(code, desc) }))
    .sort(safeSort);
}

function buildAllPayersCodeList(payers: PayerContract[]): { code: string; description: string }[] {
  const seen = new Map<string, string>();
  payers.forEach(p => {
    p.reimbursement.rates?.forEach(r => {
      if (!r.cpt_hcpcs) return; // skip null/empty codes
      if (!seen.has(r.cpt_hcpcs)) {
        seen.set(r.cpt_hcpcs, r.description);
      }
    });
  });
  return Array.from(seen.entries())
    .map(([code, desc]) => ({ code, description: codeDescription(code, desc) }))
    .sort(safeSort);
}

// ─── Single Payer Rate Table ─────────────────────────────────────────────────

const SinglePayerRates: React.FC<{ payer: PayerContract }> = ({ payer }) => {
  const theme = useTheme();
  const rates = payer.reimbursement.rates || [];

  // Discover tiers
  const tiers = useMemo(() => {
    const set = new Set<string>();
    rates.forEach(r => { if (r.provider_tier) set.add(r.provider_tier); });
    return Array.from(set).sort();
  }, [rates]);

  const [selectedTier, setSelectedTier] = useState<string>(tiers[0] || '');

  const tierOptions = tiers.map(t => ({ value: t, label: t.charAt(0) + t.slice(1).toLowerCase() }));

  // Build lookup for selected tier
  const tierRateMap = useMemo(() => {
    const map = new Map<string, CodeRate>();
    rates.forEach(r => {
      if (r.cpt_hcpcs && r.provider_tier === selectedTier) {
        map.set(r.cpt_hcpcs, r);
      }
    });
    return map;
  }, [rates, selectedTier]);

  // Build code list from actual data for this tier
  const codeList = useMemo(() => buildCodeList(rates, selectedTier), [rates, selectedTier]);

  return (
    <Box>
      {/* Tier Selector */}
      {tiers.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <SegmentedControl
            options={tierOptions}
            value={selectedTier}
            onChange={setSelectedTier}
            size="small"
          />
        </Box>
      )}

      {/* Methodology Banner */}
      <Box sx={{ mb: 2, py: 1, px: 1.5, bgcolor: theme.palette.grey[50], borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {payer.reimbursement.rate_formula_description}
        </Typography>
        {payer.reimbursement._source && (
          <CitationChip
            citation={Array.isArray(payer.reimbursement._source) ? payer.reimbursement._source[0] : payer.reimbursement._source}
            documentRegistry={payer.document_registry}
          />
        )}
      </Box>

      {/* Rate Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1 }}>CPT</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Allowed</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Effective</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {codeList.map(({ code, description }) => {
              const rate = tierRateMap.get(code);
              return (
                <TableRow key={code} hover sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500, py: 1.25 }}>{code}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{description}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                    {rate ? formatCurrency(rate.allowed_amount) : '\u2014'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {rate ? formatDate(rate.effective_date) : ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── All Payers Comparison ───────────────────────────────────────────────────

const AllPayersComparison: React.FC<{ payers: PayerContract[] }> = ({ payers }) => {
  const theme = useTheme();

  // Discover all tiers across every payer, normalize to lowercase for matching
  const allTiers = useMemo(() => {
    const tierSet = new Set<string>();
    payers.forEach(p => {
      p.reimbursement.rates?.forEach(r => {
        if (r.provider_tier) tierSet.add(r.provider_tier);
      });
    });
    return Array.from(tierSet).sort();
  }, [payers]);

  const [selectedTier, setSelectedTier] = useState<string>(allTiers[0] || '');
  const tierOptions = allTiers.map(t => ({ value: t, label: t.charAt(0) + t.slice(1).toLowerCase() }));

  // Build rate map filtered by selected tier (fall back to first rate if payer lacks that tier)
  const rateMap = useMemo(() => {
    const map = new Map<string, Map<string, CodeRate>>();
    payers.forEach(p => {
      const cptMap = new Map<string, CodeRate>();
      // First pass: add rates matching the selected tier
      p.reimbursement.rates?.forEach(r => {
        if (r.cpt_hcpcs && r.provider_tier === selectedTier && !cptMap.has(r.cpt_hcpcs)) {
          cptMap.set(r.cpt_hcpcs, r);
        }
      });
      // Second pass: fill gaps with any tier (so payers that don't use that tier name still show rates)
      p.reimbursement.rates?.forEach(r => {
        if (r.cpt_hcpcs && !cptMap.has(r.cpt_hcpcs)) {
          cptMap.set(r.cpt_hcpcs, r);
        }
      });
      map.set(p.payer.name, cptMap);
    });
    return map;
  }, [payers, selectedTier]);

  // Build code list from filtered data
  const codeList = useMemo(() => {
    const seen = new Map<string, string>();
    payers.forEach(p => {
      const cptMap = rateMap.get(p.payer.name);
      cptMap?.forEach((r, code) => {
        if (!seen.has(code)) seen.set(code, r.description);
      });
    });
    return Array.from(seen.entries())
      .map(([code, desc]) => ({ code, description: codeDescription(code, desc) }))
      .sort(safeSort);
  }, [payers, rateMap]);

  const medians = useMemo(() => {
    const m = new Map<string, number>();
    codeList.forEach(({ code }) => {
      const amounts: number[] = [];
      payers.forEach(p => {
        const r = rateMap.get(p.payer.name)?.get(code);
        if (r?.allowed_amount != null) amounts.push(r.allowed_amount);
      });
      if (amounts.length > 0) {
        amounts.sort((a, b) => a - b);
        const mid = Math.floor(amounts.length / 2);
        m.set(code, amounts.length % 2 === 0 ? (amounts[mid - 1] + amounts[mid]) / 2 : amounts[mid]);
      }
    });
    return m;
  }, [payers, rateMap, codeList]);

  function getCellBg(value: number | null | undefined, median: number | null): string {
    if (value == null || median == null) return theme.palette.grey[50];
    const ratio = value / median;
    if (ratio >= 1.1) return theme.palette.success.light + '40';
    if (ratio >= 0.9) return theme.palette.grey[100];
    return theme.palette.error.light + '30';
  }

  return (
    <Box>
      {/* Tier Selector */}
      {allTiers.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <SegmentedControl
            options={tierOptions}
            value={selectedTier}
            onChange={setSelectedTier}
            size="small"
          />
        </Box>
      )}

      <TableContainer sx={{ maxHeight: 700, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, bgcolor: 'background.paper', zIndex: 2 }}>CPT</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, bgcolor: 'background.paper', zIndex: 2 }}>Description</TableCell>
              {payers.map(p => (
                <TableCell key={p.payer.name} align="right" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, bgcolor: 'background.paper', zIndex: 2 }}>{p.payer.name}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {codeList.map(({ code, description }) => {
              const median = medians.get(code) || null;
              return (
                <TableRow key={code} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{code}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{description}</TableCell>
                  {payers.map(p => {
                    const rate = rateMap.get(p.payer.name)?.get(code);
                    const val = rate?.allowed_amount;
                    return (
                      <Tooltip key={p.payer.name} title={rate?.rate_formula || rate?.description || ''} arrow>
                        <TableCell align="right" sx={{ bgcolor: getCellBg(val, median), fontWeight: val != null ? 500 : 400, fontFamily: 'monospace', cursor: 'help' }}>
                          {val != null ? formatCurrency(val) : '\u2014'}
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Main Export ─────────────────────────────────────────────────────────────

interface RatesTabProps {
  payers: PayerContract[];
  selectedPayer: PayerContract | null;
}

export const RatesTab: React.FC<RatesTabProps> = ({ payers, selectedPayer }) => {
  if (selectedPayer) {
    return <SinglePayerRates payer={selectedPayer} />;
  }
  return <AllPayersComparison payers={payers} />;
};
